import { Injectable } from '@angular/core';
import { AppConfigService } from '@app/app-config.service';

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsService {
  private mapsLoaded = false;
  private readonly scriptUrl = 'https://maps.googleapis.com/maps/api/js';

  constructor(private readonly appConfigService: AppConfigService) {}

  /**
   * Get Google Maps API key from app.config.json
   */
  private getGoogleMapsApiKey(): string {
    return this.appConfigService.get('googleMapsApiKey') || '';
  }

  /**
   * Load Google Maps library and return google.maps namespace
   */
  async loadGoogleMaps(): Promise<any> {
    if (this.mapsLoaded) {
      return (globalThis as any).google?.maps;
    }

    await this.loadScript();
    this.mapsLoaded = true;
    return (globalThis as any).google?.maps;
  }

  /**
   * Load Google Maps script dynamically with required libraries
   */
  private loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      const existingScript = document.getElementById('google-maps-script');

      if (existingScript) {
        // If script already exists, wait for google.maps to be available
        this.waitForGoogleMaps().then(resolve).catch(reject);
        return;
      }

      const apiKey = this.getGoogleMapsApiKey();

      if (!apiKey || apiKey.trim() === '') {
        reject(new Error('Google Maps API key is not configured. Please check your app.config.json'));
        return;
      }

      const scriptUrl = `${this.scriptUrl}?language=en&key=${apiKey}&libraries=places&v=weekly`;

      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.src = scriptUrl;
      script.async = true;

      const timeoutId = window.setTimeout(() => {
        reject(
          new Error(
            'Google Maps script failed to load (timeout). Verify API key validity and API permissions. Check DevTools Network tab for HTTP response.'
          )
        );
      }, 10000);

      script.onload = () => {
        // Wait a bit for google.maps to be initialized, then check
        this.waitForGoogleMaps()
          .then(() => {
            clearTimeout(timeoutId);
            resolve();
          })
          .catch(err => {
            clearTimeout(timeoutId);
            reject(err);
          });
      };

      script.onerror = error => {
        clearTimeout(timeoutId);
        const errorMsg = `Failed to load Google Maps script (network error). Error: ${error}`;
        reject(new Error(errorMsg));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Wait for google.maps to be available on window object
   */
  private waitForGoogleMaps(maxAttempts = 50): Promise<void> {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const checkGoogle = setInterval(() => {
        attempts++;
        const google = (globalThis as any).google;

        if (google?.maps?.places) {
          clearInterval(checkGoogle);
          resolve();
        } else if (attempts > maxAttempts) {
          clearInterval(checkGoogle);
          const errorMsg = 'Google Maps library failed to load within timeout. Script may have failed due to invalid API key or network error.';
          reject(new Error(errorMsg));
        }
      }, 100);
    });
  }

  /**
   * Initialize autocomplete for an input element
   */
  async initializeAutocomplete(inputElement: HTMLInputElement): Promise<any> {
    const maps = await this.loadGoogleMaps();
    return new maps.places.Autocomplete(inputElement, {
      fields: ['address_components', 'geometry', 'formatted_address'],
      types: ['geocode'],
    });
  }

  /**
   * Initialize a map on a given element
   */
  async initializeMap(containerElement: HTMLElement, options: any): Promise<any> {
    const maps = await this.loadGoogleMaps();
    return new maps.Map(containerElement, options);
  }
}
